import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { contentApi, getErrorMessage } from '../services/api';
import { queryKeys } from '../services/queryClient';
import { ContentCard, APIResponse } from '../types';
import { useDebounce } from './useDebounce';

// Hook for searching content with debouncing
export const useSearchContent = (
  query: string,
  page = 1,
  limit = 20,
  debounceMs = 300,
  options?: any
) => {
  const debouncedQuery = useDebounce(query, debounceMs);
  
  return useQuery({
    queryKey: queryKeys.content.search(debouncedQuery, page),
    queryFn: () => contentApi.searchContent(debouncedQuery, page, limit),
    enabled: !!debouncedQuery && debouncedQuery.trim().length >= 2, // Minimum 2 characters
    keepPreviousData: true,
    staleTime: 2 * 60 * 1000, // Search results are more volatile, cache for 2 minutes
    ...options,
  });
};

// Hook for infinite scrolling search results
export const useInfiniteSearchContent = (
  query: string,
  limit = 20,
  debounceMs = 300,
  options?: any
) => {
  const debouncedQuery = useDebounce(query, debounceMs);
  
  return useInfiniteQuery({
    queryKey: queryKeys.content.search(debouncedQuery),
    queryFn: ({ pageParam = 1 }) => contentApi.searchContent(debouncedQuery, pageParam, limit),
    getNextPageParam: (lastPage: APIResponse<ContentCard[]>, allPages: APIResponse<ContentCard[]>[]) => {
      if (!lastPage.pagination?.hasMore) return undefined;
      return allPages.length + 1;
    },
    enabled: !!debouncedQuery && debouncedQuery.trim().length >= 2,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

// Hook for search suggestions (returns limited results for autocomplete)
export const useSearchSuggestions = (
  query: string,
  debounceMs = 200,
  options?: any
) => {
  const debouncedQuery = useDebounce(query, debounceMs);
  
  return useQuery({
    queryKey: [...queryKeys.content.search(debouncedQuery), 'suggestions'],
    queryFn: async () => {
      const response = await contentApi.searchContent(debouncedQuery, 1, 5); // Limit to 5 suggestions
      return response.data;
    },
    enabled: !!debouncedQuery && debouncedQuery.trim().length >= 2,
    staleTime: 1 * 60 * 1000, // Suggestions cache for 1 minute
    ...options,
  });
};

// Hook for person details
export const usePersonDetails = (
  id: string,
  options?: any
) => {
  return useQuery({
    queryKey: queryKeys.persons.details(id),
    queryFn: () => contentApi.getPersonDetails(id),
    enabled: !!id,
    staleTime: 15 * 60 * 1000, // Person details rarely change
    ...options,
  });
};

// Custom hook for managing search state and history
export const useSearchState = () => {
  const [searchHistory, setSearchHistory] = React.useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('streaming-search-history');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const [recentSearches, setRecentSearches] = React.useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('streaming-recent-searches');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const addToHistory = React.useCallback((query: string) => {
    if (!query || query.trim().length < 2) return;
    
    const trimmedQuery = query.trim();
    setSearchHistory(prev => {
      const updated = [trimmedQuery, ...prev.filter(q => q !== trimmedQuery)].slice(0, 50);
      localStorage.setItem('streaming-search-history', JSON.stringify(updated));
      return updated;
    });
  }, []);
  
  const addToRecent = React.useCallback((query: string) => {
    if (!query || query.trim().length < 2) return;
    
    const trimmedQuery = query.trim();
    setRecentSearches(prev => {
      const updated = [trimmedQuery, ...prev.filter(q => q !== trimmedQuery)].slice(0, 10);
      localStorage.setItem('streaming-recent-searches', JSON.stringify(updated));
      return updated;
    });
  }, []);
  
  const clearHistory = React.useCallback(() => {
    setSearchHistory([]);
    localStorage.removeItem('streaming-search-history');
  }, []);
  
  const clearRecent = React.useCallback(() => {
    setRecentSearches([]);
    localStorage.removeItem('streaming-recent-searches');
  }, []);
  
  const removeFromHistory = React.useCallback((query: string) => {
    setSearchHistory(prev => {
      const updated = prev.filter(q => q !== query);
      localStorage.setItem('streaming-search-history', JSON.stringify(updated));
      return updated;
    });
  }, []);
  
  return {
    searchHistory,
    recentSearches,
    addToHistory,
    addToRecent,
    clearHistory,
    clearRecent,
    removeFromHistory,
  };
};

// Hook for search analytics and performance tracking
export const useSearchAnalytics = () => {
  const trackSearch = React.useCallback((query: string, resultCount: number, responseTime: number) => {
    if (import.meta.env.DEV) {
      console.log('[Search Analytics]', {
        query,
        resultCount,
        responseTime,
        timestamp: new Date().toISOString(),
      });
    }
    
    // In a real application, you would send this data to an analytics service
    // Example: analytics.track('search_performed', { query, resultCount, responseTime });
  }, []);
  
  const trackSearchClick = React.useCallback((query: string, contentId: string, position: number) => {
    if (import.meta.env.DEV) {
      console.log('[Search Click Analytics]', {
        query,
        contentId,
        position,
        timestamp: new Date().toISOString(),
      });
    }
    
    // Track which search results users click on
    // analytics.track('search_result_clicked', { query, contentId, position });
  }, []);
  
  return {
    trackSearch,
    trackSearchClick,
  };
};

// Hook for handling search errors with specific messaging
export const useSearchError = () => {
  const getSearchErrorMessage = (error: unknown, query: string): string => {
    const baseMessage = getErrorMessage(error);
    
    if (baseMessage.includes('Network')) {
      return 'Unable to search right now. Please check your connection and try again.';
    }
    
    if (baseMessage.includes('timeout')) {
      return `Search for "${query}" is taking longer than expected. Please try again.`;
    }
    
    if (baseMessage.includes('rate limit')) {
      return 'You\'re searching too quickly. Please wait a moment and try again.';
    }
    
    return `Unable to search for "${query}". Please try again.`;
  };
  
  const shouldShowNoResults = (error: unknown, query: string, isLoading: boolean): boolean => {
    // Show "no results" instead of error for certain cases
    if (isLoading) return false;
    if (!query || query.trim().length < 2) return false;
    
    // If it's a 404 or the error suggests no results found
    const errorMessage = getErrorMessage(error).toLowerCase();
    return errorMessage.includes('not found') || errorMessage.includes('no results');
  };
  
  return {
    getSearchErrorMessage,
    shouldShowNoResults,
  };
};

// Import React for hooks that use React state
import React from 'react';